import { sortCommentsAscending } from "components/common/comments/sortComments";
import { cloneDeep, isEqual } from "lodash";
import { applyPatch } from "rfc6902";
import { omitDeep } from "../../functions/omitDeep";
import { hcr_addDomainReplacement } from "./hcr_addDomainReplacement";
import { hcr_addSingleReplacement } from "./hcr_addSingleReplacement";
import { hcr_buildParentTemplates } from "./hcr_buildParentTemplates";
import { hcr_checkDRs } from "./hcr_checkDRs";
import { hcr_checkSingles } from "./hcr_checkSingles";
import { hcr_loadClinicalLibrary } from "./hcr_loadClinicalLibrary";
import { hcr_loadHierarchy } from "./hcr_loadHierarchy";
import { hcr_removeDomainReplacement } from "./hcr_removeDomainReplacement";
import { hcr_removeSingleReplacment } from "./hcr_removeSingleReplacment";
import { hcr_updateDomainReplacement } from "./hcr_updateDomainReplacement";
import { hcr_updateSingleReplacements } from "./hcr_updateSingleReplacements";
import {
  ADD_DOMAIN_REPLACEMENT,
  ADD_HIERARCHY_NODES,
  ADD_SINGLE_REPLACEMENT,
  HierarchyContextProps,
  IHierarchyAction,
  LOAD_CLINICAL_LIBRARY,
  LOAD_HIERARCHY,
  PATCH_HIERARCHY_COMMENTS,
  REMOVE_DOMAIN_REPLACEMENT,
  REMOVE_HIERARCHY_COMMENT,
  REMOVE_HIERARCHY_NODE,
  REMOVE_SINGLE_REPLACEMENT,
  SAVE_HIERARCHY,
  SAVE_HIERARCHY_COMPLETED,
  SAVE_HIERARCHY_FAILED,
  SAVE_TOC_MAJOR_VERSION,
  SET_ACCESS_RIGHTS,
  SET_CLINICAL_LIBRARY_LOADING,
  SET_HIERARCHY_COMMENT_PANEL,
  SET_HIERARCHY_ID,
  SET_HIERARCHY_NAME_SAVE_BLOCKER,
  SET_HIERARCHY_SUBSCRIPTION_STATUS,
  UPDATE_DOMAIN_REPLACEMENT,
  UPDATE_HIERARCHY,
  UPDATE_HIERARCHY_COMMENT,
  UPDATE_SINGLE_REPLACEMENT,
  UPDATE_TLF_ITEM_INDEX,
} from "./interface";

/**
 * Reducer for hierarchy context state transitions.
 *
 * @param state - The current `HierarchyContextProps` state.
 * @param action - The `IHierarchyAction` describing the change to apply.
 * @returns The updated `HierarchyContextProps` after applying the action.
 */
export const hierarchyContextReducer = (
  state: HierarchyContextProps,
  action: IHierarchyAction,
): HierarchyContextProps => {
  let result: HierarchyContextProps = state;

  switch (action.operation) {
    case ADD_DOMAIN_REPLACEMENT: {
      let newState = hcr_addDomainReplacement(action, state);
      newState = hcr_checkDRs(newState);
      result = newState;
      break;
    }
    case ADD_HIERARCHY_NODES: {
      const updatedHierarchyNodes = [
        ...state.hierarchyNodes.filter((hn) => !action.hierarchyNodes!.find((h) => h.id === hn.id)),
        ...action.hierarchyNodes,
      ];
      result = {
        ...state,
        hierarchyNodes: updatedHierarchyNodes,
        parentTemplates: state.hierarchy
          ? hcr_buildParentTemplates(state.hierarchy, updatedHierarchyNodes)
          : state.parentTemplates,
      };
      break;
    }
    case ADD_SINGLE_REPLACEMENT: {
      let newState = hcr_addSingleReplacement(action, state);
      newState = hcr_checkSingles(newState);
      result = newState;
      break;
    }
    case LOAD_HIERARCHY: {
      // Run load hierarchy function
      const loadedState = hcr_loadHierarchy(action, state);
      // TypeScript assertion: hierarchy is guaranteed to be non-null after hcr_loadHierarchy
      const checkedDRs = hcr_checkDRs(loadedState);
      const checkedSingles = hcr_checkSingles(checkedDRs);
      result = {
        ...checkedSingles,
        isNameChanged: false,
      };
      break;
    }
    case LOAD_CLINICAL_LIBRARY: {
      // Run load clinical library function
      const loadedState = hcr_loadClinicalLibrary(action, state);
      result = { ...loadedState, isClinicalLibraryLoading: false };
      break;
    }
    case PATCH_HIERARCHY_COMMENTS: {
      if (!state.hierarchy)
        throw new Error(`hierarchyContextReducer: ${action.operation}: Hierarchy not assigned`);
      // Apply the patch to the hierarchy comments
      const commentsPatch = action.patch;
      /* istanbul ignore else */
      if (commentsPatch && commentsPatch.length > 0) {
        // Need to clone deep as applyPatch modifies the object
        const newComments = cloneDeep(state.hierarchy.comments).sort(sortCommentsAscending);
        applyPatch(newComments, commentsPatch);
        result = {
          ...state,
          hierarchy: {
            ...state.hierarchy,
            comments: newComments,
          },
        };
      }
      break;
    }
    case REMOVE_DOMAIN_REPLACEMENT: {
      let newState = hcr_removeDomainReplacement(action, state);
      newState = hcr_checkDRs(newState);
      result = newState;
      break;
    }
    case REMOVE_HIERARCHY_COMMENT: {
      if (!state.hierarchy)
        throw new Error(`hierarchyContextReducer: ${action.operation}: Hierarchy not assigned`);
      result = {
        ...state,
        hierarchy: {
          ...state.hierarchy,
          comments: state.hierarchy.comments.filter((c) => c.id !== action.commentId),
        },
      };
      break;
    }
    case REMOVE_HIERARCHY_NODE: {
      result = {
        ...state,
        hierarchyNodes: state.hierarchyNodes.filter((hn) => hn.id !== action.hierarchyId),
      };
      break;
    }
    case REMOVE_SINGLE_REPLACEMENT: {
      let newState = hcr_removeSingleReplacment(action, state);
      newState = hcr_checkSingles(newState);
      result = newState;
      break;
    }
    case SAVE_HIERARCHY:
      result = { ...state, doSave: true };
      break;
    case SAVE_HIERARCHY_COMPLETED: {
      const filteredHierarchyNodes = [
        ...state.hierarchyNodes
          // Need to remove old hierarchy node from list, and parent's child reference
          .filter((hn) => hn.id !== action.hierarchy.id && hn.id !== action.oldHierarchyId)
          .map((hn) => {
            if (action.oldHierarchyId && hn.id === action.hierarchy.parentId) {
              return {
                ...hn,
                children: [
                  ...hn.children.filter((cid) => cid.id !== action.oldHierarchyId),
                  {
                    id: action.hierarchy.id,
                    hierarchyTag: hn.hierarchyTag,
                    level: hn.level,
                    name: action.hierarchy.name,
                    parentTag: hn.parentTag,
                    type: hn.type,
                    tocMajorVersion: action.hierarchy.tocMajorVersion,
                    tocMinorVersion: action.hierarchy.tocMinorVersion,
                  },
                ],
              };
            }
            return hn;
          }),
        action.hierarchy,
      ];

      const loadLikeAction: IHierarchyAction = {
        operation: LOAD_HIERARCHY,
        hierarchy: action.hierarchy,
        hierarchyNodes: [],
        accessRights: state.accessRights,
        isSubscribed: state.isSubscribed,
      };

      // Normalise
      const loadedState = hcr_loadHierarchy(loadLikeAction, {
        ...state,
        hierarchyNodes: filteredHierarchyNodes,
      });

      // Perform checks
      const checkedDRs = hcr_checkDRs(loadedState);
      const checkedSingles = hcr_checkSingles(checkedDRs);

      result = {
        ...checkedSingles,
        isNameChanged: false,
        doSave: false,
        doTocSave: false,
        hierarchyNodes: filteredHierarchyNodes,
      };
      break;
    }
    case SAVE_HIERARCHY_FAILED:
      result = {
        ...state,
        doSave: false,
        doTocSave: false,
        saveTocDescription: undefined,
      };
      break;
    case SAVE_TOC_MAJOR_VERSION:
      result = {
        ...state,
        doTocSave: true,
        saveTocDescription: action.saveTocDescription,
        hierarchyNodes: state.hierarchyNodes.filter((hn) => hn.id !== action.oldHierarchyId),
      };
      break;
    case SET_CLINICAL_LIBRARY_LOADING:
      result = {
        ...state,
        isClinicalLibraryLoading: action.isClinicalLibraryLoading,
      };
      break;
    case SET_ACCESS_RIGHTS:
      result = {
        ...state,
        accessRights: action.accessRights,
        doGetAccessRights: false,
      };
      break;
    case SET_HIERARCHY_COMMENT_PANEL:
      result = {
        ...state,
        commentPanelStatus: {
          ...state.commentPanelStatus,
          ...action.commentPanelStatus,
        },
      };
      break;
    case SET_HIERARCHY_ID:
      result = { ...state, id: action.hierarchyId };
      break;
    case SET_HIERARCHY_NAME_SAVE_BLOCKER:
      result = {
        ...state,
        saveBlockers: {
          ...state.saveBlockers,
          hierarchyName: action.warningText ?? undefined,
        },
      };
      break;
    case UPDATE_DOMAIN_REPLACEMENT: {
      let newState = hcr_updateDomainReplacement(action, state);
      newState = hcr_checkDRs(newState);
      result = newState;
      break;
    }
    case UPDATE_HIERARCHY: {
      if (!state.hierarchy)
        throw new Error(`hierarchyContextReducer: ${action.operation}: Hierarchy not assigned`);
      const newState = {
        ...state,
        hierarchy: {
          ...state.hierarchy,
          ...action.hierarchy,
        },
        isNameChanged:
          typeof action.isNameChanged === "boolean" ? action.isNameChanged : state.isNameChanged,
      };
      if (action.savedHierarchy) {
        newState.savedHierarchy = cloneDeep(action.savedHierarchy);
      }
      const checkedDRs = hcr_checkDRs(newState);
      const checkedSingles = hcr_checkSingles(checkedDRs);
      result = checkedSingles;
      break;
    }

    case UPDATE_HIERARCHY_COMMENT: {
      if (!state.hierarchy)
        throw new Error(`hierarchyContextReducer: ${action.operation}: Hierarchy not assigned`);
      result = {
        ...state,
        hierarchy: {
          ...state.hierarchy,
          comments: [
            ...state.hierarchy.comments.filter((c) => c.id !== action.comment.id),
            action.comment,
          ],
        },
      };
      break;
    }
    case UPDATE_SINGLE_REPLACEMENT: {
      let newState = hcr_updateSingleReplacements(action, state);
      newState = hcr_checkSingles(newState);
      result = newState;
      break;
    }
    case UPDATE_TLF_ITEM_INDEX: {
      if (!state.hierarchy)
        throw new Error(`hierarchyContextReducer: ${action.operation}: Hierarchy not assigned`);
      const newState = {
        ...state,
        hierarchy: { ...state.hierarchy, tocMinorVersion: action.tocMinorVersion },
        tlfItemIndex: action.tlfItemIndex,
      };
      result = newState;
      break;
    }
    case SET_HIERARCHY_SUBSCRIPTION_STATUS: {
      result = { ...state, isSubscribed: action.isSubscribed };
      break;
    }
  }

  // Check if hierarchy has changed (but skip for cases that already set changed explicitly)
  if (
    action.operation !== (SAVE_HIERARCHY_COMPLETED as IHierarchyAction["operation"]) &&
    action.operation !== (SAVE_HIERARCHY as IHierarchyAction["operation"]) &&
    action.operation !== (SAVE_HIERARCHY_FAILED as IHierarchyAction["operation"]) &&
    action.operation !== (SAVE_TOC_MAJOR_VERSION as IHierarchyAction["operation"]) &&
    action.operation !== (LOAD_HIERARCHY as IHierarchyAction["operation"])
  ) {
    const changed = !isEqual(
      omitDeep(result.hierarchy, [
        "comments",
        "tocMinorVersion",
        "sections",
        "children",
        "studyStandardsDate",
        "history",
        "clinicalLibrary",
      ]),
      omitDeep(result.savedHierarchy, [
        "comments",
        "tocMinorVersion",
        "sections",
        "children",
        "studyStandardsDate",
        "history",
        "clinicalLibrary",
      ]),
    );
    if (changed !== result.changed) {
      result = { ...result, changed };
    }
  }

  return result;
};
